import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, DonationCampaignRow } from '../api';
import { planEndpoints } from '../api-plans';
import type { CollectionMethod, Pledge, PledgeInterval } from '../api-plans';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table } from '../ui';
import type { Tone } from '../ui';
import { INPUT, dollars, humanize, toCents } from './event/shared';

/**
 * Pledges (D-356), on the Donations page: record one, follow it, change it.
 *
 * The totals here are the only place pledged money is added up, and they are
 * labeled as pledged. The Raised figure above, the thermometer, the donor wall
 * and every statement count only what was paid, because each payment against a
 * pledge becomes an ordinary gift when — and only when — it arrives.
 */

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const toIsoDay = (local: string) => new Date(`${local}T12:00:00`).toISOString();
const pledgeTone = (p: Pick<Pledge, 'status' | 'overdue'>): Tone => (p.overdue ? 'warn' : p.status === 'FULFILLED' ? 'ok' : p.status === 'CANCELED' ? 'neutral' : 'accent');
const installmentTone = (status: string): Tone => (status === 'PAID' ? 'ok' : status === 'OVERDUE' ? 'warn' : status === 'CANCELED' ? 'neutral' : 'accent');
const INTERVALS: [PledgeInterval, string][] = [['MONTH', 'Monthly'], ['QUARTER', 'Quarterly'], ['YEAR', 'Yearly']];

export function PledgesPanel({ campaigns, canManage }: { campaigns: DonationCampaignRow[]; canManage: boolean }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [campaignFilter, setCampaignFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const list = useQuery({ queryKey: ['pledges', campaignFilter, statusFilter], queryFn: () => planEndpoints.pledges(campaignFilter || null, statusFilter || null) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['pledges'] });
  const totals = list.data?.totals;
  return (
    <Panel
      title="Pledges"
      description={`Gifts promised and paid on a schedule. Only what has been paid counts as given anywhere else on this page. ${totals ? `Pledged ${dollars(totals.pledged_cents)} · paid ${dollars(totals.paid_cents)} · outstanding ${dollars(totals.outstanding_cents)} · ${totals.overdue} overdue.` : ''}`}
      actions={
        <div className="flex flex-wrap gap-2">
          <select className={INPUT} value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} aria-label="Campaign">
            <option value="">All campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select className={INPUT} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Pledge status">
            <option value="">Any status</option>
            <option value="ACTIVE">Active</option>
            <option value="FULFILLED">Fulfilled</option>
            <option value="CANCELED">Canceled</option>
          </select>
          {canManage && !creating ? <Button variant="primary" onClick={() => setCreating(true)}>New pledge</Button> : null}
        </div>
      }
    >
      {error ? <div className="px-5 pt-4"><ErrorNote>{error}</ErrorNote></div> : null}
      {notice ? <p className="mx-5 mt-4 rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}
      {creating ? (
        <PledgeForm
          campaigns={campaigns}
          onCancel={() => setCreating(false)}
          onDone={(url) => {
            setCreating(false);
            setError(null);
            setNotice(`Pledge recorded. The donor's link, to send another way if you need to: ${url}`);
            refresh();
          }}
          onError={setError}
        />
      ) : null}
      <Table headers={['Donor', 'Campaign', 'Pledged', 'Paid', 'Outstanding', 'Next', 'Status', '']}>
        {(list.data?.pledges ?? []).length === 0 ? (
          <EmptyRow columns={8}>No pledges match.</EmptyRow>
        ) : (
          list.data!.pledges.map((p) => (
            <PledgeRowView key={p.id} pledge={p} open={open === p.id} onToggle={() => setOpen(open === p.id ? null : p.id)} canManage={canManage} onChanged={(message) => { setError(null); setNotice(message); refresh(); }} onError={setError} />
          ))
        )}
      </Table>
    </Panel>
  );
}

function PledgeRowView({ pledge: p, open, onToggle, canManage, onChanged, onError }: { pledge: Pledge; open: boolean; onToggle: () => void; canManage: boolean; onChanged: (message: string) => void; onError: (message: string) => void }) {
  const percent = p.total_cents > 0 ? Math.min(100, Math.round((p.paid_cents / p.total_cents) * 100)) : 0;
  return (
    <>
      <tr className="border-b border-line">
        <td className="px-5 py-3">
          <div className="font-medium text-ink-900">{p.donor.name ?? p.donor.email}</div>
          <div className="text-xs text-ink-400">{p.donor.email}</div>
        </td>
        <td className="px-5 py-3 text-sm text-ink-600">{p.campaign.name}</td>
        <td className="tabular px-5 py-3 text-sm">{dollars(p.total_cents)}</td>
        <td className="tabular px-5 py-3 text-sm">
          {dollars(p.paid_cents)}
          <div className="text-xs text-ink-400">{percent}%</div>
        </td>
        <td className="tabular px-5 py-3 text-sm">{dollars(p.outstanding_cents)}</td>
        <td className="px-5 py-3 text-sm text-ink-600">{p.next_due && p.status === 'ACTIVE' ? `${dollars(p.next_due.amount_cents)} · ${day(p.next_due.due_at)}` : '—'}</td>
        <td className="px-5 py-3">
          <Badge tone={pledgeTone(p)}>{p.overdue ? 'overdue' : humanize(p.status)}</Badge>
          <div className="mt-1 text-xs text-ink-400">{p.collection_method === 'AUTOMATIC' ? (p.card_saved ? 'card on file' : 'card not saved yet') : 'email link'}</div>
        </td>
        <td className="px-5 py-3 text-right">
          <Button onClick={onToggle}>{open ? 'Close' : 'Open'}</Button>
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-line">
          <td colSpan={8} className="bg-page px-5 py-4">
            <PledgeDetail id={p.id} canManage={canManage} onChanged={onChanged} onError={onError} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function PledgeDetail({ id, canManage, onChanged, onError }: { id: string; canManage: boolean; onChanged: (message: string) => void; onError: (message: string) => void }) {
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ['pledges', 'detail', id], queryFn: () => planEndpoints.pledge(id) });
  const [total, setTotal] = useState('');
  const [remainingCount, setRemainingCount] = useState('');
  const [remainingFrom, setRemainingFrom] = useState('');
  const [remainingInterval, setRemainingInterval] = useState<PledgeInterval>('MONTH');
  const [method, setMethod] = useState<CollectionMethod | ''>('');
  const [reason, setReason] = useState('');
  const done = (message: string) => {
    setReason('');
    void queryClient.invalidateQueries({ queryKey: ['pledges'] });
    onChanged(message);
  };
  const adjust = useMutation({
    mutationFn: () =>
      planEndpoints.adjustPledge(id, {
        ...(total ? { total_cents: toCents(total) ?? 0 } : {}),
        ...(remainingCount && remainingFrom ? { remaining: { installment_count: Number(remainingCount), first_due_at: toIsoDay(remainingFrom), interval: Number(remainingCount) === 1 ? null : remainingInterval } } : {}),
        ...(method ? { collection_method: method } : {}),
        reason,
      }),
    onSuccess: () => done('Pledge changed. What was already given is unchanged.'),
    onError: (cause: ApiError) => onError(cause.message),
  });
  const cancel = useMutation({ mutationFn: () => planEndpoints.cancelPledge(id, reason), onSuccess: () => done('Pledge canceled. Nothing more will be asked for or charged; what was given stays given.'), onError: (cause: ApiError) => onError(cause.message) });
  const link = useMutation({ mutationFn: () => planEndpoints.pledgeLink(id), onSuccess: (r) => onChanged(`The donor's link: ${r.url}`), onError: (cause: ApiError) => onError(cause.message) });
  const p = detail.data?.pledge;
  if (!p) return <p className="text-sm text-ink-400">Loading…</p>;
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-600">
        {p.installment_count === 1 ? 'One payment' : `${p.installment_count} payments, ${humanize(p.interval)}`}. Recorded {day(p.created_at)}.
        {p.note ? ` Note: ${p.note}` : ''}
        {p.cancel_reason ? ` Canceled ${day(p.canceled_at)}: ${p.cancel_reason}.` : ''}
        {p.fulfilled_at ? ` Fulfilled ${day(p.fulfilled_at)}.` : ''}
      </p>
      {p.card_consent_text ? <p className="text-xs text-ink-400">Card consent, {day(p.card_consented_at)}: “{p.card_consent_text}”</p> : null}
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs text-ink-400 uppercase">
            <th className="py-1.5 pr-4">#</th>
            <th className="py-1.5 pr-4">Due</th>
            <th className="py-1.5 pr-4">Amount</th>
            <th className="py-1.5 pr-4">Status</th>
            <th className="py-1.5 pr-4">Reminder</th>
            <th className="py-1.5">Notes</th>
          </tr>
        </thead>
        <tbody>
          {(p.installments ?? []).map((i) => (
            <tr key={i.id} className="border-b border-line">
              <td className="py-1.5 pr-4">{i.sequence}</td>
              <td className="py-1.5 pr-4 whitespace-nowrap">{day(i.due_at)}</td>
              <td className="tabular py-1.5 pr-4">{dollars(i.amount_cents)}</td>
              <td className="py-1.5 pr-4">
                <Badge tone={installmentTone(i.status)}>{humanize(i.status)}</Badge>
                {i.paid_at ? <span className="ml-1 text-xs text-ink-400">{day(i.paid_at)}</span> : null}
              </td>
              <td className="py-1.5 pr-4 text-xs text-ink-400">{i.reminder_sent_at ? `sent ${day(i.reminder_sent_at)}` : i.remind_at ? `due ${day(i.remind_at)}` : '—'}</td>
              <td className="py-1.5 text-xs text-ink-600">{i.last_failure_message ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage && p.status === 'ACTIVE' ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="New total" htmlFor={`pl-total-${id}`} hint={`At least ${dollars(p.paid_cents)}, already given`}>
              <input id={`pl-total-${id}`} type="number" step="0.01" min={0} value={total} onChange={(e) => setTotal(e.target.value)} className={`${INPUT} w-28`} placeholder={(p.total_cents / 100).toFixed(2)} />
            </Field>
            <Field label="Re-plan what is left" htmlFor={`pl-count-${id}`} hint="Payments, from a date">
              <div className="flex gap-2">
                <input id={`pl-count-${id}`} type="number" min={1} max={60} value={remainingCount} onChange={(e) => setRemainingCount(e.target.value)} className={`${INPUT} w-20`} placeholder="#" />
                <input type="date" value={remainingFrom} onChange={(e) => setRemainingFrom(e.target.value)} className={INPUT} aria-label="First remaining payment" />
                <select value={remainingInterval} onChange={(e) => setRemainingInterval(e.target.value as PledgeInterval)} className={INPUT} aria-label="How often">
                  {INTERVALS.map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </Field>
            <Field label="Collection" htmlFor={`pl-method-${id}`}>
              <select id={`pl-method-${id}`} value={method} onChange={(e) => setMethod(e.target.value as CollectionMethod | '')} className={INPUT}>
                <option value="">Unchanged</option>
                <option value="EMAIL_LINK">Email a link each time</option>
                <option value="AUTOMATIC">Charge the saved card</option>
              </select>
            </Field>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Reason" htmlFor={`pl-reason-${id}`} hint="Recorded in the audit log">
              <input id={`pl-reason-${id}`} value={reason} onChange={(e) => setReason(e.target.value)} className={`${INPUT} w-72`} />
            </Field>
            <Button variant="primary" disabled={!reason.trim() || adjust.isPending || (!total && !(remainingCount && remainingFrom) && !method)} onClick={() => adjust.mutate()}>
              Save changes
            </Button>
            <Button variant="danger" disabled={!reason.trim() || cancel.isPending} onClick={() => cancel.mutate()}>
              Cancel pledge
            </Button>
            <Button disabled={link.isPending} onClick={() => link.mutate()}>
              Show donor link
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PledgeForm({ campaigns, onDone, onCancel, onError }: { campaigns: DonationCampaignRow[]; onDone: (url: string) => void; onCancel: () => void; onError: (message: string) => void }) {
  const active = campaigns.filter((c) => c.active);
  const [campaignId, setCampaignId] = useState(active[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [count, setCount] = useState('12');
  const [interval, setInterval] = useState<PledgeInterval>('MONTH');
  const [first, setFirst] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<CollectionMethod>('EMAIL_LINK');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [listing, setListing] = useState<'NAME' | 'ANONYMOUS' | 'IN_MEMORY' | ''>('');
  const [memory, setMemory] = useState('');
  const [note, setNote] = useState('');
  const cents = toCents(amount) ?? 0;
  const n = Math.max(1, Number(count) || 1);
  const each = Math.floor(cents / n);
  const create = useMutation({
    mutationFn: () =>
      planEndpoints.createPledge({
        campaign_id: campaignId,
        total_cents: cents,
        installment_count: n,
        interval: n === 1 ? null : interval,
        first_due_at: toIsoDay(first),
        collection_method: method,
        donor_name: name.trim() || null,
        donor_email: email.trim(),
        wall_listing: listing || null,
        in_memory_of: listing === 'IN_MEMORY' ? memory.trim() || null : null,
        note: note.trim() || null,
      }),
    onSuccess: (r) => onDone(r.url),
    onError: (cause: ApiError) => onError(cause.message),
  });
  return (
    <div className="space-y-3 border-b border-line px-5 py-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Campaign" htmlFor="np-campaign">
          <select id="np-campaign" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className={`${INPUT} w-full`}>
            {active.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Donor name" htmlFor="np-name">
          <input id="np-name" value={name} onChange={(e) => setName(e.target.value)} className={`${INPUT} w-full`} />
        </Field>
        <Field label="Donor email" htmlFor="np-email" hint="Reminders, links and receipts go here">
          <input id="np-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${INPUT} w-full`} />
        </Field>
        <Field label="Total pledged" htmlFor="np-amount" hint={cents > 0 ? (n === 1 ? `One payment of ${dollars(cents)}` : `${n} × ${dollars(each)}, the last ${dollars(cents - each * (n - 1))}`) : 'Dollars'}>
          <input id="np-amount" type="number" step="0.01" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} className={`${INPUT} w-full`} />
        </Field>
        <Field label="Payments" htmlFor="np-count" hint="1 for a single payment on the date">
          <div className="flex gap-2">
            <input id="np-count" type="number" min={1} max={60} value={count} onChange={(e) => setCount(e.target.value)} className={`${INPUT} w-20`} />
            <select value={interval} onChange={(e) => setInterval(e.target.value as PledgeInterval)} className={INPUT} disabled={n === 1} aria-label="How often">
              {INTERVALS.map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label="First payment" htmlFor="np-first">
          <input id="np-first" type="date" value={first} onChange={(e) => setFirst(e.target.value)} className={`${INPUT} w-full`} />
        </Field>
        <Field label="How it is collected" htmlFor="np-method" hint={method === 'AUTOMATIC' ? 'The donor saves a card, with their consent, the first time they pay from their link; until then each payment is asked for by email.' : 'A reminder a week ahead and a notice on the day, each with the donor’s link.'}>
          <select id="np-method" value={method} onChange={(e) => setMethod(e.target.value as CollectionMethod)} className={`${INPUT} w-full`}>
            <option value="EMAIL_LINK">Email a link each time</option>
            <option value="AUTOMATIC">Charge a saved card</option>
          </select>
        </Field>
        <Field label="Donor wall" htmlFor="np-wall" hint="Only paid money is ever listed">
          <select id="np-wall" value={listing} onChange={(e) => setListing(e.target.value as typeof listing)} className={`${INPUT} w-full`}>
            <option value="">Not listed</option>
            <option value="NAME">By name</option>
            <option value="ANONYMOUS">Anonymously</option>
            <option value="IN_MEMORY">In memory of…</option>
          </select>
        </Field>
        {listing === 'IN_MEMORY' ? (
          <Field label="In memory of" htmlFor="np-memory">
            <input id="np-memory" value={memory} onChange={(e) => setMemory(e.target.value)} className={`${INPUT} w-full`} />
          </Field>
        ) : null}
      </div>
      <Field label="Note" htmlFor="np-note">
        <input id="np-note" value={note} onChange={(e) => setNote(e.target.value)} className={`${INPUT} w-full`} />
      </Field>
      <div className="flex gap-2">
        <Button variant="primary" disabled={!campaignId || cents <= 0 || !email.trim() || create.isPending} onClick={() => create.mutate()}>
          Record pledge
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
